import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  SyncOutlined
} from "@ant-design/icons";
import { Modal, Progress, Skeleton, Table, Tabs, Tag } from "antd";
import React from "react";

import type {
  ProjectStage,
  ProjectStagesData,
  ProjectStageTask
} from "./types";
import type { ColumnsType } from "antd/es/table";

export interface ProjectStagesViewProps {
  loading: boolean;
  data?: ProjectStagesData;
}

interface ProcessedStage {
  key: string;
  stage: string;
  plot: string;
  department: "Groundworks" | "Roads & Sewers";
  completedTasks: number;
  totalTasks: number;
  status: "Completed" | "In Progress" | "Not Started";
  qaStatus: string | null;
  progress: number;
  tasks: ProjectStageTask[];
}

const TAB_KEYS = {
  all: "all",
  completed: "completed",
  inProgress: "in-progress",
  notStarted: "not-started"
} as const;

const TAB_ITEMS = [
  { key: TAB_KEYS.all, label: "All Stages" },
  { key: TAB_KEYS.completed, label: "Completed" },
  { key: TAB_KEYS.inProgress, label: "In Progress" },
  { key: TAB_KEYS.notStarted, label: "Not Started" }
];

const deriveStageStatus = (
  completed: number,
  total: number,
  fallback: string | undefined
): ProcessedStage["status"] => {
  if (total > 0 && completed === total) return "Completed";
  if (completed > 0) return "In Progress";
  return (fallback as ProcessedStage["status"]) || "Not Started";
};

const getStatusIcon = (status: string) => {
  if (status === "Completed") {
    return <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 16 }} />;
  }
  if (status === "In Progress") {
    return <SyncOutlined style={{ color: "#1890ff", fontSize: 16 }} />;
  }
  return <ClockCircleOutlined style={{ color: "#8c8c8c", fontSize: 16 }} />;
};

/**
 * Stage View — Tabs (status filter) + Table (one row per stage per plot/run)
 * + Modal (tasks for the selected stage).
 *
 * Pure-presentational: feeds purely off `data`. Shared by MJ-Extended-PM's
 * forecasting overview and MJ-Tracker's project stages tab.
 */
export const ProjectStagesView: React.FC<ProjectStagesViewProps> = ({
  loading,
  data
}) => {
  const [activeTab, setActiveTab] = React.useState<string>(TAB_KEYS.all);
  const [isModalVisible, setIsModalVisible] = React.useState(false);
  const [selectedStage, setSelectedStage] =
    React.useState<ProcessedStage | null>(null);

  const processedStages = React.useMemo<ProcessedStage[]>(() => {
    if (!data) return [];

    const stages: ProcessedStage[] = [];

    const pushFromGroupings = (
      groupings: { plotNo: string; stages: ProjectStage[] }[],
      department: ProcessedStage["department"]
    ) => {
      groupings?.forEach((grouping) => {
        grouping.stages?.forEach((stage) => {
          const totalTasks = stage.tasks?.length ?? 0;
          const completedTasks =
            stage.tasks?.filter((t) => t.currentStatus === "Completed")
              .length ?? 0;
          const progress =
            totalTasks > 0
              ? Math.round((completedTasks / totalTasks) * 100)
              : 0;

          stages.push({
            key: `${stage.stageName}-${grouping.plotNo}`,
            stage: stage.stageName,
            plot: grouping.plotNo,
            department,
            completedTasks,
            totalTasks,
            status: deriveStageStatus(
              completedTasks,
              totalTasks,
              stage.stageStatus
            ),
            qaStatus: stage.qaStatus,
            progress,
            tasks: stage.tasks ?? []
          });
        });
      });
    };

    pushFromGroupings(data.groundworks?.plots ?? [], "Groundworks");
    pushFromGroupings(
      (data.roadsAndSewers?.runs ?? []).map((run) => ({
        plotNo: run.runNo,
        stages: run.stages
      })),
      "Roads & Sewers"
    );

    return stages;
  }, [data]);

  const filteredStages = React.useMemo(() => {
    switch (activeTab) {
      case TAB_KEYS.completed:
        return processedStages.filter((s) => s.status === "Completed");
      case TAB_KEYS.inProgress:
        return processedStages.filter((s) => s.status === "In Progress");
      case TAB_KEYS.notStarted:
        return processedStages.filter((s) => s.status === "Not Started");
      default:
        return processedStages;
    }
  }, [processedStages, activeTab]);

  const columns: ColumnsType<ProcessedStage> = [
    {
      title: "Stage",
      dataIndex: "stage",
      key: "stage",
      render: (stage: string, record) => (
        <div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{stage}</div>
          <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 8 }}>
            Plot {record.plot} ({record.completedTasks}/{record.totalTasks}{" "}
            tasks completed)
          </div>
          <div>
            <Tag
              color="blue"
              style={{
                border: "1px solid #1890ff",
                background: "#e6f7ff",
                marginRight: 8
              }}
            >
              ⚒ {record.department}
            </Tag>
            <Tag
              style={{
                border: "1px solid #d9d9d9",
                background: "white",
                color: "#595959"
              }}
            >
              {stage}
            </Tag>
          </div>
        </div>
      )
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      width: 200,
      render: (status: ProcessedStage["status"]) => {
        const color =
          status === "Completed"
            ? "success"
            : status === "In Progress"
              ? "processing"
              : "default";
        const backgroundColor =
          status === "Completed"
            ? "#f6ffed"
            : status === "In Progress"
              ? "#e6f7ff"
              : "#fafafa";
        const borderColor =
          status === "Completed"
            ? "#b7eb8f"
            : status === "In Progress"
              ? "#91d5ff"
              : "#d9d9d9";
        const textColor =
          status === "Completed"
            ? "#52c41a"
            : status === "In Progress"
              ? "#1890ff"
              : "#8c8c8c";

        return (
          <Tag
            color={color}
            style={{
              background: backgroundColor,
              border: `1px solid ${borderColor}`,
              color: textColor,
              fontWeight: 500
            }}
          >
            {status}
          </Tag>
        );
      }
    },
    {
      title: "QA Status",
      dataIndex: "qaStatus",
      key: "qaStatus",
      width: 200,
      render: (qaStatus: string | null) => {
        if (!qaStatus) {
          return (
            <Tag
              style={{
                background: "#fafafa",
                border: "1px solid #d9d9d9",
                color: "#8c8c8c"
              }}
            >
              Pending
            </Tag>
          );
        }
        return (
          <Tag
            color="success"
            style={{
              background: "#f6ffed",
              border: "1px solid #b7eb8f",
              color: "#52c41a"
            }}
          >
            {qaStatus}
          </Tag>
        );
      }
    },
    {
      title: "Progress",
      dataIndex: "progress",
      key: "progress",
      width: 300,
      render: (progress: number) => (
        <div style={{ padding: "0 8px" }}>
          <Progress
            percent={progress}
            strokeColor={progress === 100 ? "#52c41a" : "#1890ff"}
            format={(percent) => (
              <span style={{ fontSize: 14, fontWeight: 500, color: "#4b5563" }}>
                {percent}%
              </span>
            )}
            style={{ marginBottom: 0, flex: 1 }}
          />
        </div>
      )
    },
    {
      title: "Action",
      key: "actions",
      align: "center",
      width: 100,
      render: (_, record) => (
        <EyeOutlined
          style={{ fontSize: 18, color: "#1890ff", cursor: "pointer" }}
          onClick={() => {
            setSelectedStage(record);
            setIsModalVisible(true);
          }}
        />
      )
    }
  ];

  return (
    <div>
      {loading && <Skeleton loading />}
      {data && (
        <>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={TAB_ITEMS}
          />
          <Table<ProcessedStage>
            columns={columns}
            dataSource={filteredStages}
            rowKey="key"
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              showTotal: (total, range) => (
                <span
                  style={{ fontSize: 14, color: "#4b5563", marginRight: 16 }}
                >
                  {`${range[0]}-${range[1]} of ${total} Stages`}
                </span>
              )
            }}
          />
        </>
      )}

      <Modal
        title={
          <div style={{ borderBottom: "1px solid #f0f0f0", paddingBottom: 8 }}>
            <div style={{ fontSize: 18, fontWeight: 600 }}>Stage Tasks</div>
          </div>
        }
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setSelectedStage(null);
        }}
        footer={null}
        width={600}
      >
        {selectedStage && (
          <div>
            {selectedStage.tasks.map((task, index) => {
              const taskStatus = task.currentStatus || task.status || "";
              const isCompleted = taskStatus === "Completed";
              const isInProgress = taskStatus === "In Progress";
              const borderColor = isCompleted
                ? "#52c41a"
                : isInProgress
                  ? "#1890ff"
                  : "#d9d9d9";
              const backgroundColor = isCompleted
                ? "#f6ffed"
                : isInProgress
                  ? "#e6f7ff"
                  : "#fafafa";

              return (
                <div
                  key={task.taskId ?? index}
                  style={{
                    border: `2px solid ${borderColor}`,
                    borderLeft: `4px solid ${borderColor}`,
                    borderRadius: 8,
                    padding: 16,
                    marginBottom: 12,
                    background: backgroundColor
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: 16,
                          fontWeight: 600,
                          marginBottom: 4
                        }}
                      >
                        {task.taskName}
                      </div>
                      <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                        Task {index + 1} of {selectedStage.totalTasks}
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8
                      }}
                    >
                      {getStatusIcon(taskStatus)}
                      <Tag
                        color={
                          isCompleted
                            ? "success"
                            : isInProgress
                              ? "processing"
                              : "default"
                        }
                        style={{
                          background: isCompleted
                            ? "#f6ffed"
                            : isInProgress
                              ? "#e6f7ff"
                              : "#fafafa",
                          border: `1px solid ${borderColor}`,
                          color: isCompleted
                            ? "#52c41a"
                            : isInProgress
                              ? "#1890ff"
                              : "#8c8c8c",
                          fontWeight: 500
                        }}
                      >
                        {taskStatus}
                      </Tag>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Modal>
    </div>
  );
};
